import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { API } from "@/App";
import axios from "axios";
import { Trophy, Crown, Medal, TrendingUp, ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";

const LeaderboardPage = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const res = await axios.get(`${API}/leaderboard`);
        setLeaderboard(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, []);

  const getRankIcon = (rank) => {
    switch(rank) {
      case 1: return <Crown className="w-6 h-6 text-yellow-400" />;
      case 2: return <Medal className="w-6 h-6 text-gray-400" />;
      case 3: return <Medal className="w-6 h-6 text-amber-600" />;
      default: return <span className="w-6 h-6 flex items-center justify-center text-muted-foreground font-bold">#{rank}</span>;
    }
  };

  const getRankBg = (rank) => {
    switch(rank) {
      case 1: return "bg-gradient-to-r from-yellow-500/20 to-transparent border-yellow-500/30";
      case 2: return "bg-gradient-to-r from-gray-400/20 to-transparent border-gray-400/30";
      case 3: return "bg-gradient-to-r from-amber-600/20 to-transparent border-amber-600/30";
      default: return "bg-card/30 border-white/10";
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] relative">
      {/* Ambient red/blue glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-[20%] -left-[20%] w-[70%] h-[50%] rounded-full bg-red-600/20 blur-[100px]" />
        <div className="absolute -bottom-[20%] -right-[20%] w-[70%] h-[50%] rounded-full bg-blue-600/25 blur-[100px]" />
      </div>
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Link 
          to="/browse" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to Debates
        </Link>

        <div className="flex items-center gap-4 mb-12">
          <div className="w-14 h-14 rounded-lg bg-accent/20 flex items-center justify-center">
            <Trophy className="w-7 h-7 text-accent" />
          </div>
          <div>
            <h1 className="text-4xl font-bold">Leaderboard</h1>
            <p className="text-muted-foreground">Top debaters ranked by wins</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : leaderboard.length === 0 ? (
          <div className="text-center py-24 bg-card/30 rounded-lg border border-white/10">
            <Trophy className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">No Rankings Yet</h3>
            <p className="text-muted-foreground">Complete debates to appear on the leaderboard!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {leaderboard.map((entry) => (
              <Link
                key={entry.user_id}
                to={`/profile/${entry.user_id}`}
                data-testid={`leaderboard-entry-${entry.rank}`}
                className={`flex items-center gap-4 p-4 rounded-lg border transition-all hover:scale-[1.01] ${getRankBg(entry.rank)}`}
              >
                <div className="w-12 flex justify-center">
                  {getRankIcon(entry.rank)}
                </div>
                
                <div className={`p-1 rounded-full stage-glow-${entry.stage_color || 'red'}`}>
                  <Avatar className="w-14 h-14 border-2 border-white/20">
                    <AvatarImage src={entry.picture} />
                    <AvatarFallback className={`text-lg ${
                      entry.stage_color === 'blue' ? 'bg-[#3b82f6]' :
                      entry.stage_color === 'purple' ? 'bg-purple-500' :
                      entry.stage_color === 'green' ? 'bg-green-500' :
                      'bg-[#e11d48]'
                    }`}>
                      {entry.username[0].toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </div>
                
                <div className="flex-1">
                  <p className="font-bold text-lg">{entry.username}</p>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>{entry.total_debates} debates</span>
                    <span className="flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      {entry.win_rate}% win rate
                    </span>
                  </div>
                </div>
                
                <div className="text-right">
                  <p className="text-2xl font-bold mono text-accent">{entry.wins}</p>
                  <p className="text-xs text-muted-foreground uppercase">Wins</p>
                </div>
                
                {entry.rank <= 3 && (
                  <Badge className={`ml-4 ${
                    entry.rank === 1 ? 'bg-yellow-500 text-black' :
                    entry.rank === 2 ? 'bg-gray-400 text-black' :
                    'bg-amber-600 text-black'
                  }`}>
                    {entry.rank === 1 ? 'Champion' : entry.rank === 2 ? 'Runner Up' : 'Bronze'}
                  </Badge>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaderboardPage;
